import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

export type AudioStep = 'ready' | 'recording' | 'pause' | 'waiting';
export type StepFlowType =
  | 'basic'
  | 'pauseable'
  | 'oneClick'
  | 'recordOnly'
  | 'instantRecord';

interface RecorderState {
  step: AudioStep;
  recorder: MediaRecorder | null;
  analyzer: AnalyserNode | null;
  isRecording: boolean;
  file: File | null;
  audioUrl: string;
}

export interface RecorderInitState {
  step: AudioStep;
  isRecording: boolean;
  file: File | null;
}

type RecorderAction =
  | { type: 'SET_STEP'; payload: AudioStep }
  | { type: 'SET_RECORDER'; payload: MediaRecorder | null }
  | { type: 'SET_ANALYZER'; payload: AnalyserNode | null }
  | { type: 'SET_IS_RECORDING'; payload: boolean }
  | { type: 'SET_FILE'; payload: File | null }
  | { type: 'SET_AUDIO_URL'; payload: string }
  | { type: 'INIT_STATE'; payload: RecorderInitState };

const initialState: RecorderState = {
  step: 'ready',
  recorder: null,
  analyzer: null,
  isRecording: false,
  file: null,
  audioUrl: '',
};

function recorderReducer(state: RecorderState, action: RecorderAction): RecorderState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload };
    case 'SET_RECORDER':
      return { ...state, recorder: action.payload };
    case 'SET_ANALYZER':
      return { ...state, analyzer: action.payload };
    case 'SET_IS_RECORDING':
      return { ...state, isRecording: action.payload };
    case 'SET_FILE':
      return { ...state, file: action.payload };
    case 'SET_AUDIO_URL':
      return { ...state, audioUrl: action.payload };
    case 'INIT_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

interface RecorderContextType {
  state: RecorderState;
  startRecording: (onComplete?: (file: File) => void) => Promise<void>;
  stopRecording: (isSubmit: boolean) => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  initRecordingState: (state: RecorderInitState) => void;
}

const RecorderContext = createContext<RecorderContextType | undefined>(undefined);

interface RecorderProviderProps {
  children: ReactNode;
}

export const RecorderProvider = ({ children }: RecorderProviderProps) => {
  const [state, dispatch] = useReducer(recorderReducer, initialState);

  const startRecording = useCallback(async (onComplete?: (file: File) => void) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioChunks: Blob[] = [];

      const recorder = new MediaRecorder(stream);

      recorder.addEventListener('dataavailable', event => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      });

      recorder.addEventListener('stop', () => {
        const blob = new Blob(audioChunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, {
          type: 'audio/wav',
        });
        const url = URL.createObjectURL(file);
        dispatch({ type: 'SET_FILE', payload: file });
        dispatch({ type: 'SET_AUDIO_URL', payload: url });

        onComplete?.(file);
      });

      // AudioContext 연결
      const audioContext = new AudioContext();
      const analyzer = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyzer);
      analyzer.fftSize = 256;

      dispatch({ type: 'SET_ANALYZER', payload: analyzer });
      dispatch({ type: 'SET_RECORDER', payload: recorder });

      recorder.addEventListener('start', () => {
        dispatch({ type: 'SET_STEP', payload: 'recording' });
      });

      recorder.start();
    } catch (error) {
      console.error('🎤 마이크 권한 오류:', error);
    }
  }, []);

  const stopRecording = useCallback((isSubmit: boolean) => {
    const { recorder, analyzer } = state;
    recorder?.stop();
    recorder?.stream.getTracks().forEach(track => track.stop());
    if (analyzer?.context.state !== 'closed') {
      (analyzer?.context as AudioContext)
        ?.close()
        .catch(e => console.warn('AudioContext already closed', e));
    }
    if (isSubmit) {
      dispatch({ type: 'SET_STEP', payload: 'ready' });
    } else {
      dispatch({ type: 'SET_STEP', payload: 'waiting' });
    }
  }, [state]);

  const pauseRecording = useCallback(() => {
    const { recorder } = state;
    recorder?.pause();
    dispatch({ type: 'SET_STEP', payload: 'pause' });
  }, [state]);

  const resumeRecording = useCallback(() => {
    const { recorder } = state;
    if (recorder?.state === 'paused') {
      recorder?.resume();
      dispatch({ type: 'SET_STEP', payload: 'recording' });
    }
  }, [state]);

  const initRecordingState = useCallback((initState: RecorderInitState) => {
    dispatch({ type: 'INIT_STATE', payload: initState });
  }, []);

  const value: RecorderContextType = {
    state,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    initRecordingState,
  };

  return (
    <RecorderContext.Provider value={value}>
      {children}
    </RecorderContext.Provider>
  );
};

export const useRecorderInnerState = () => {
  const context = useContext(RecorderContext);
  if (context === undefined) {
    throw new Error('useRecorderInnerState must be used within a RecorderProvider');
  }
  return {
    step: context.state.step,
    isRecording: context.state.isRecording,
    analyzer: context.state.analyzer,
  };
};

export const useRecorderAction = () => {
  const context = useContext(RecorderContext);
  if (context === undefined) {
    throw new Error('useRecorderAction must be used within a RecorderProvider');
  }
  return {
    startRecording: context.startRecording,
    stopRecording: context.stopRecording,
    pauseRecording: context.pauseRecording,
    resumeRecording: context.resumeRecording,
  };
};

export const useInitRecorder = (props: RecorderInitState) => {
  const context = useContext(RecorderContext);
  if (context === undefined) {
    throw new Error('useInitRecorder must be used within a RecorderProvider');
  }

  const firstRender = React.useRef(false);
  if (!firstRender.current) {
    firstRender.current = true;
    context.initRecordingState(props);
  }
};
